import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, getCurrentUser, refreshSession, getCurrentSession } from './lib/supabase';

type AuthContextType = {
  user: User | null;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true
});

export const useAuth = () => {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First try to get an existing session
        const session = await getCurrentSession();
        
        if (session?.user?.id && session?.access_token) {
          setUser(session.user);
        } else {
          // No valid session, try to refresh
          const refreshedSession = await refreshSession();
          
          if (refreshedSession?.user?.id && refreshedSession?.access_token) {
            setUser(refreshedSession.user);
          } else {
            // No valid session after refresh
            setUser(null);
            // Clear any invalid auth data
            await supabase.auth.signOut();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        // Clear any invalid auth data
        await supabase.auth.signOut();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, {
        hasSession: !!session,
        hasUserId: !!session?.user?.id,
        hasAccessToken: !!session?.access_token
      });
      
      if (event === 'SIGNED_IN' && session?.user) {
        if (session.user.id && session.access_token) {
          setUser(session.user);
        } else {
          console.error('Invalid session data on sign in');
          setUser(null);
          await supabase.auth.signOut();
        }
      } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setUser(null);
        // Clear any stored auth data
        localStorage.removeItem('sb-auth-token');
        sessionStorage.clear();
      } else if (event === 'TOKEN_REFRESHED') {
        if (session?.user?.id && session?.access_token) {
          setUser(session.user);
        } else {
          const refreshedSession = await refreshSession();
          if (refreshedSession?.user?.id && refreshedSession?.access_token) {
            setUser(refreshedSession.user);
          } else {
            setUser(null);
            await supabase.auth.signOut();
          }
        }
      }
      
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}